import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  Briefcase,
  Coins,
  HelpCircle,
  KeyRound,
  ListTodo,
  MessageSquare,
  Sparkles,
  Terminal,
  Wand2,
  Zap,
  FileText,
  TrendingUp,
} from "lucide-react";

import { Logo } from "./logo";
import { openCookieConsent } from "./cookie-consent";
import { ROLE_GUIDE_LINKS } from "@/content/role-guide-links";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full border-t border-border bg-background">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-6">
          {/* Brand Info */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <Link
              to="/"
              className="flex items-center gap-2.5 font-semibold text-foreground hover:opacity-90 transition-opacity"
            >
              <Logo className="size-8 rounded-md shadow-sm" />
              <span className="text-lg font-semibold">JobCues</span>
            </Link>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Tailor your resume to any job description in seconds with evidence-based ATS
              optimization, verified jobs directory, and intelligent tracking.
            </p>

            {/* Social Media Links */}
            <div className="mt-4 flex items-center gap-2">
              <a
                href="https://x.com/usejobcues"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter / X"
                className="flex size-8 items-center justify-center rounded-lg border border-border bg-card/60 text-muted-foreground transition-colors hover:border-brand/40 hover:bg-brand/10 hover:text-foreground"
              >
                <TwitterIcon className="size-3.5" />
              </a>
              <a
                href="https://instagram.com/usejobcues"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex size-8 items-center justify-center rounded-lg border border-border bg-card/60 text-muted-foreground transition-colors hover:border-brand/40 hover:bg-brand/10 hover:text-foreground"
              >
                <InstagramIcon className="size-4" />
              </a>
              <a
                href="https://linkedin.com/company/jobcues"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="flex size-8 items-center justify-center rounded-lg border border-border bg-card/60 text-muted-foreground transition-colors hover:border-brand/40 hover:bg-brand/10 hover:text-foreground"
              >
                <LinkedinIcon className="size-4" />
              </a>
            </div>

            <div className="mt-6">
              <TinyshelfBadge />
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold text-foreground">Product</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <Link
                  to="/resume-tailor"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <Wand2 className="size-4 text-muted-foreground shrink-0" />
                  Resume Tailor
                </Link>
              </li>
              <li>
                <Link
                  to="/ats-booster"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <Zap className="size-4 text-muted-foreground shrink-0" />
                  ATS Booster
                </Link>
              </li>
              <li>
                <Link
                  to="/jobs"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <Briefcase className="size-4 text-muted-foreground shrink-0" />
                  Jobs Directory
                </Link>
              </li>
              <li>
                <Link
                  to="/job-tracker"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <ListTodo className="size-4 text-muted-foreground shrink-0" />
                  Application Tracker
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold text-foreground">Resources</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <Link
                  to="/jobs/tech-hiring-trends"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <TrendingUp className="size-4 text-muted-foreground shrink-0" />
                  Tech Hiring Trends
                </Link>
              </li>
              <li>
                <Link
                  to="/resume"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <FileText className="size-4 text-muted-foreground shrink-0" />
                  Resume Guides
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <HelpCircle className="size-4 text-muted-foreground shrink-0" />
                  FAQ & Guide
                </Link>
              </li>
            </ul>
          </div>

          {/* Account & Profile */}
          <div>
            <h4 className="font-semibold text-foreground">Account</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <Link
                  to="/account"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <Sparkles className="size-4 text-muted-foreground shrink-0" />
                  Master Profile
                </Link>
              </li>
              <li>
                <Link
                  to="/credits"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <Coins className="size-4 text-muted-foreground shrink-0" />
                  Credits & Usage
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <MessageSquare className="size-4 text-muted-foreground shrink-0" />
                  Contact us
                </Link>
              </li>
            </ul>
          </div>

          {/* Developers & MCP */}
          <div>
            <h4 className="font-semibold text-foreground">Developers</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <Link
                  to="/api-docs"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <BookOpen className="size-4 text-muted-foreground shrink-0" />
                  API Documentation
                </Link>
              </li>
              <li>
                <Link
                  to="/api-keys"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <KeyRound className="size-4 text-muted-foreground shrink-0" />
                  API Keys
                </Link>
              </li>
              <li>
                <Link
                  to="/api-docs"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                >
                  <Terminal className="size-4 text-muted-foreground shrink-0" />
                  MCP Server
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Every role guide gets a link from every page. Twelve new URLs will
            not get crawled from a hub page alone. */}
        <nav aria-label="Resume guides" className="mt-10 border-t border-border pt-6">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Resume guides by role
          </h4>
          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
            {ROLE_GUIDE_LINKS.map((guide) => (
              <li key={guide.slug}>
                <Link
                  to="/resume/$role"
                  params={{ role: guide.slug }}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {guide.name} resume
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom divider & copyright */}
        <div className="mt-10 border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <p>© {currentYear} JobCues. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground/80">
            <Link
              to="/terms"
              className="hover:text-foreground transition-colors underline-offset-4 hover:underline"
            >
              Terms & Conditions
            </Link>
            <span>•</span>
            <Link
              to="/privacy"
              className="hover:text-foreground transition-colors underline-offset-4 hover:underline"
            >
              Privacy Policy
            </Link>
            <span>•</span>
            <button
              type="button"
              onClick={openCookieConsent}
              className="hover:text-foreground transition-colors underline-offset-4 hover:underline cursor-pointer"
            >
              Cookie Preferences
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function InstagramIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

function TwitterIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedinIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

export function TinyshelfBadge() {
  return (
    <a href="https://www.tinyshelf.co/?ref=jobcues.com" title="Featured on tinyshelf">
      <img
        src="https://www.tinyshelf.co/badge/tinyshelf-badge-dark-f4d1216a.svg"
        alt="Featured on tinyshelf"
        width={216}
        height={64}
      />
    </a>
  );
}
